import {useState} from 'react';
import {Check, Pencil} from 'lucide-react';
import {Button} from '@/components/ui/Button';
import {getSegmentDefinition} from './lib/dictionary';
import {isFieldVisible, SegmentField} from './SegmentField';
import {IssueBadges} from './CollapsibleSection';
import {Explain} from './Explain';
import {countIssues} from './lib/sections';
import type {EdiSegment} from './lib/tokenizer';
import type {EdiTransactionExplained} from './lib/explainedEnvelope';
import type {ElementPath} from './lib/document-editor';
import type {ValidationFinding} from './lib/validator';

interface SegmentGroupProps {
  segment: EdiSegment;
  /** This segment's finding-path base, e.g. "groups[0].transactions[0].header.beg". */
  basePath: string;
  groupIndex: number;
  transactionIndex: number;
  transaction: EdiTransactionExplained;
  findings: ValidationFinding[];
  onEdit: (path: ElementPath, value: string) => void;
}

/** One segment, read-only by default: a heading (id, name — its purpose on
 * hover — and issue counts), then its non-empty values as aligned
 * label/value rows. The Edit button switches just this segment to compact
 * inputs for every element present.
 * Shared by every data section so a segment looks the same everywhere. */
export function SegmentGroup({
  segment,
  basePath,
  groupIndex,
  transactionIndex,
  transaction,
  findings,
  onEdit,
}: SegmentGroupProps) {
  const [editing, setEditing] = useState(false);
  const definition = getSegmentDefinition(segment.id);

  const positions = segment.elements
    .map((_, idx) => idx + 1)
    .filter(p => editing || isFieldVisible(segment, p, basePath, findings));

  const fields = positions.map((position, i) => (
    <SegmentField
      key={position}
      groupIndex={groupIndex}
      transactionIndex={transactionIndex}
      transaction={transaction}
      segment={segment}
      position={position}
      basePath={basePath}
      findings={findings}
      editing={editing}
      autoFocus={editing && i === 0}
      onEdit={onEdit}
    />
  ));

  const editToggle = (
    <Button
      variant="ghost"
      size="sm"
      className="ml-auto h-6 shrink-0 px-2"
      aria-label={editing ? `Done editing ${segment.id}` : `Edit ${segment.id}`}
      onClick={() => setEditing(e => !e)}
    >
      {editing ? <Check className="h-3 w-3" /> : <Pencil className="h-3 w-3" />}
      {editing ? 'Done' : 'Edit'}
    </Button>
  );

  return (
    <div
      id={`segment-${basePath}`}
      tabIndex={-1}
      className="py-2.5 outline-none"
    >
      <div className="flex items-center gap-2">
        <span className="w-7 shrink-0 font-mono text-[11px] text-muted-foreground">
          {segment.id}
        </span>
        <Explain
          content={
            <p className="text-muted-foreground">{definition.purpose}</p>
          }
        >
          <h3 className="cursor-help text-sm font-semibold">
            {definition.name}
          </h3>
        </Explain>
        <IssueBadges issues={countIssues(findings, [basePath])} />
        {editToggle}
      </div>
      {fields.length > 0 && (
        <div className="mt-1.5 grid gap-x-8 pl-9 2xl:grid-cols-2">{fields}</div>
      )}
    </div>
  );
}
